import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Flex, Box } from 'rebass/styled-components';
import AttributeStatic from './AttributeStatic';
import AttributeFunction from './AttributeFunction';
import EditorName from '../Editor/EditorName';
import EditorField from '../Editor/editor-field';
import { Loading } from '..';
import api from '../../api';
import EditorLabel from '../Editor/EditorLabel';
import EditorContainer from '../Editor/EditorContainer';
import { useAppState } from '../../hooks';

const ATTRIBUTE_WIDTH = 3;

const parseType = (type) => (type === 'faas' ? 'function' : 'static');

function AttributeEditor(props) {
  const {
    isEditing,
    temporaryObject,
    brandId,
    addNotification,
    validateObject,
    themeMode,
    setSelectedObject,
    includeSdeAttribute,
  } = props;

  const [customAttributes, setCustomAttributes] = useState(null);
  const [faasAttributes, setFaasAttributes] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const isMounted = useRef(false);
  const { isCcsV2, isLoading: isAppStateLoading } = useAppState();

  const isFaas = temporaryObject.type === 'faas';

  const selectType = (name) => {
    const newObject = { ...temporaryObject };
    newObject.payload = {};
    if (name === 'function') {
      newObject.type = 'faas';
      if (isCcsV2) {
        newObject.payload = { body: { parameters: '' } };
      } else {
        newObject.payload = { body: { payload: '' } };
      }
    } else {
      newObject.type = name;
      newObject.payload = '';
    }
    validateObject(newObject);
  };

  const fetchSystemContext = async () => {
    try {
      return isCcsV2
        ? await api.ccsV2.getSystemContext(brandId, 'conversation')
        : await api.mavenContext.getSystemContext(brandId, 'conversation');
    } catch (error) {
      // There is currently no implementation to distinguish between a brand that does not exist,
      // and a brand that has no custom attributes yet... so for the moment, we ignore this error
      if (error.response && error.response.status !== 404) {
        addNotification(error, 'error');
      }
      return [];
    }
  };

  const fetchSdeAttributes = async () => {
    if (!includeSdeAttribute) {
      return [];
    }
    try {
      const sdeAttributes = [];
      const data = await api.mavenContext.getSdeList(brandId);
      data.forEach((sdeData) => (
        sdeAttributes.push(sdeData.attribute)
      ));
      return sdeAttributes;
    } catch (error) {
      if (error.response && error.response.status !== 404) {
        addNotification(error, 'error');
      }
      return [];
    }
  };

  const fetchCustomContexts = async () => {
    try {
      return isCcsV2
        ? await api.ccsV2.getCustomContexts(brandId)
        : await api.mavenConfig.getCustomContexts(brandId);
    } catch (error) {
      // There is currently no implementation to distinguish between a brand that does not exist,
      // and a brand that has no custom attributes yet... so for the moment, we ignore this error
      if (error.response && error.response.status !== 404) {
        addNotification(error, 'error');
      }
      return [];
    }
  };

  const fetchCustom = async () => {
    const [conversationData, customData, sdeData] = await Promise.all([
      fetchSystemContext(),
      fetchCustomContexts(),
      fetchSdeAttributes(),
    ]);

    const customNames = isCcsV2
      ? customData.map((item) => `custom.${item.varName}`)
      : customData.map((item) => `custom.${item.name}`);

    if (isMounted.current) {
      setCustomAttributes(customNames.concat(conversationData).concat(sdeData));
    }
  };

  const fetchFunctions = async () => {
    try {
      const data = await api.faasData.getFaasIdAndName(brandId);
      if (isMounted.current) {
        setFaasAttributes(data);
      }
    } catch (error) {
      setFaasAttributes([]);
      addNotification('Error fetching intents', 'error');
    }
  };

  const getAttributes = async () => {
    if (isMounted.current) {
      setIsLoading(true);
    }

    // Fetch all data in parallel to reduce wait time
    await Promise.all([
      fetchCustom(),
      fetchFunctions(),
    ]);

    if (isMounted.current) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      await getAttributes();
    }
    if (!isAppStateLoading) {
      fetchData();
    }
  // TODO: We have a lot of hook dependency issues here. Cannot resolve them now.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppStateLoading]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []); // eslint-disable-line

  if (isLoading || faasAttributes === null || customAttributes === null) {
    return (
      <Flex align="center" justifyContent="center" py={3}>
        <Loading data-testid="loading-attribute-editor" size={24} />
      </Flex>
    );
  }

  const attributeEditor = () => {
    if (isFaas) {
      return (
        <AttributeFunction
          isEditing={isEditing}
          temporaryObject={temporaryObject}
          customAttributes={customAttributes}
          faasAttributes={faasAttributes}
          validateObject={validateObject}
          themeMode={themeMode}
          setSelectedObject={setSelectedObject}
        />
      );
    }
    return (
      <AttributeStatic
        isEditing={isEditing}
        temporaryObject={temporaryObject}
        validateObject={validateObject}
        themeMode={themeMode}
        setSelectedObject={setSelectedObject}
      />
    );
  };

  return (
    <Flex flexDirection="column" mb={2} data-testid="attribute-editor-container">
      <EditorName
        disabled={!temporaryObject.isTemporary}
        isEditing={isEditing}
        temporaryObject={temporaryObject}
        validateObject={validateObject}
      />
      <EditorContainer variant="inner" p={3} mt={3} data-testid="editor-type">
        <EditorLabel>Type</EditorLabel>
        <Box flex={ATTRIBUTE_WIDTH}>
          <EditorField
            isEditing={isEditing}
            value={parseType(temporaryObject.type)}
            selectItem={(item) => selectType(item)}
            items={['static', 'function']}
            noFreeText
          />
        </Box>
      </EditorContainer>
      {attributeEditor()}
    </Flex>
  );
}

AttributeEditor.propTypes = {
  brandId: PropTypes.string.isRequired,
  addNotification: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
  includeSdeAttribute: PropTypes.bool.isRequired,
  temporaryObject: PropTypes.object.isRequired,
  validateObject: PropTypes.func.isRequired,
  themeMode: PropTypes.string.isRequired,
  setSelectedObject: PropTypes.func.isRequired,
};

export default AttributeEditor;
